import { Point3d } from 'app/model/client/CORE';
import { Coordinate } from 'ol/coordinate';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Extent } from 'ol/extent';
import { Airblock3d, Airspace, LatLongBounds, LatLonPoint } from 'app/model/CORE';
import { LinearRing, Polygon } from 'ol/geom';
import { default as ol, MapBrowserEvent } from 'ol';
import { click, shiftKeyOnly } from 'ol/events/condition';
import { Interaction } from 'ol/interaction';

export default class OLUtils {
    public static EMPTY_COORDINATES: Coordinate[][] = [[]];

    static toCoordinateArrayFromPoint3d(array: Point3d[]): Coordinate[] {
        const toReturn: Coordinate[] = [];
        array.map((p, index) => toReturn.push([p.x, p.y, p.z]));
        return toReturn;
    }

    static isEmpty(layer: VectorLayer<VectorSource>): boolean {
        if (layer && layer.getSource() && layer.getSource() instanceof VectorSource) {
            return layer.getSource().getFeatures().length === 0;
        }
        return true;
    }

    static getExtent(layer: VectorLayer<VectorSource>): Extent {
        if (layer && layer.getSource() && layer.getSource() instanceof VectorSource) {
            const extent = layer.getSource().getExtent();
            for (let i = 0; i < extent.length; i++) {
                if (!isFinite(extent[i])) return null;
            }
            return extent;
        }
        return null;
    }

    static buildPolygon(ab3: Airblock3d): Polygon {
        if (!ab3 || !ab3.airblock) return null;
        const polygon: Polygon = new Polygon(OLUtils.EMPTY_COORDINATES);
        polygon.appendLinearRing(new LinearRing(ab3.airblock.points.map(abPt => [abPt.longitude, abPt.latitude])));
        return polygon;
    }

    static buildPolygons(airspace: Airspace): Polygon[] {
        let polygons: Polygon[] = [];
        for (const airspace1 of airspace.composedAirspace) {
            polygons = polygons.concat(this.buildPolygons(airspace1));
        }
        for (const ab3 of airspace.airblocks) {
            if (ab3.airblock && ab3.airblock.points) {
                polygons.push(this.buildPolygon(ab3));
            }
        }
        return polygons;
    }

    static buildPolygonFromArray(coords: Array<LatLonPoint>): Polygon {
        const polygon: Polygon = new Polygon(OLUtils.EMPTY_COORDINATES);
        polygon.appendLinearRing(new LinearRing(coords.map(abPt => [abPt.longitude, abPt.latitude])));
        return polygon;
    }

    static getExtentLowerLeftX(extent: Extent): number {
        if (extent.length > 0) {
            return extent[0];
        }
        return 0.0;
    }

    static getExtentLowerLeftY(extent: Extent): number {
        if (extent.length > 1) {
            return extent[1];
        }
        return 0.0;
    }

    static getExtentUpperRightX(extent: Extent): number {
        if (extent.length > 2) {
            return extent[2];
        }
        return 0.0;
    }

    static getExtentUpperRightY(extent: Extent): number {
        if (extent.length > 3) {
            return extent[3];
        }
        return 0.0;
    }

    static getExtentLagLongBounds(extent: Extent): LatLongBounds {
        return {
            minLat: this.getExtentLowerLeftY(extent),
            maxLat: this.getExtentUpperRightY(extent),
            minLon: this.getExtentLowerLeftX(extent),
            maxLon: this.getExtentUpperRightX(extent),
        };
    }

    static buildBox(posn: LatLonPoint, width: number): Polygon {
        const points: Coordinate[] = [];
        const delta = width / 2;
        points[0] = [posn.longitude - delta, posn.latitude - delta];
        points[1] = [posn.longitude + delta, posn.latitude - delta];
        points[2] = [posn.longitude + delta, posn.latitude + delta];
        points[3] = [posn.longitude - delta, posn.latitude + delta];
        points[4] = points[0];
        const lr: LinearRing = new LinearRing(points);
        const polygon = new Polygon(this.EMPTY_COORDINATES);
        polygon.appendLinearRing(lr);
        return polygon;
    }

    static getRouteSegmentArrow(startCoordinate: Coordinate, endCoordinate: Coordinate): Polygon {
        const middleX = (endCoordinate[0] + startCoordinate[0]) / 2;
        const middleY = (endCoordinate[1] + startCoordinate[1]) / 2;

        const deltaX = endCoordinate[0] - startCoordinate[0];
        const deltaY = endCoordinate[1] - startCoordinate[1];
        const angleRad = Math.atan2(deltaY, deltaX);

        const height = 0.03;
        const width = height * 0.8;
        const shift = 0.05;

        const arrow: Polygon = OLUtils.buildTriangle([middleX, middleY], angleRad, height, width, shift);

        const segmentLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (segmentLength < (shift + height) * 2) {
            return null; // Segment is too short to display an arrow
        }

        return arrow;
    }

    static buildTriangle(base: Coordinate, angleRad: number, height: number, width: number, shift: number): Polygon {
        const shiftX = Math.cos(angleRad) * shift;
        const shiftY = Math.sin(angleRad) * shift;

        const topX = base[0] + Math.cos(angleRad) * height + shiftX;
        const topY = base[1] + Math.sin(angleRad) * height + shiftY;
        const topCoordinate: Coordinate = [topX, topY];

        const leftX = base[0] + (Math.cos(angleRad - Math.PI / 2) * width) / 2 + shiftX;
        const leftY = base[1] + (Math.sin(angleRad - Math.PI / 2) * width) / 2 + shiftY;
        const leftCoordinate: Coordinate = [leftX, leftY];

        const rightX = base[0] + (Math.cos(angleRad + Math.PI / 2) * width) / 2 + shiftX;
        const rightY = base[1] + (Math.sin(angleRad + Math.PI / 2) * width) / 2 + shiftY;
        const rightCoordinate: Coordinate = [rightX, rightY];

        const arrow: Polygon = new Polygon(OLUtils.EMPTY_COORDINATES);
        arrow.appendLinearRing(new LinearRing([rightCoordinate, leftCoordinate, topCoordinate, rightCoordinate]));

        return arrow;
    }

    static isShiftAndClick(object: MapBrowserEvent<MouseEvent>) {
        const clicked = click(object);
        const shifted = shiftKeyOnly(object);
        return clicked && shifted;
    }

    public static toPolygon(points: Array<LatLonPoint>): Polygon {
        const polygon: Polygon = new Polygon(OLUtils.EMPTY_COORDINATES);
        polygon.appendLinearRing(new LinearRing(points.map(abPt => [abPt.longitude, abPt.latitude])));
        return polygon;
    }

    public static getCenterCoordinate(extent: Extent): Coordinate {
        const x =
            OLUtils.getExtentLowerLeftX(extent) +
            (OLUtils.getExtentUpperRightX(extent) - OLUtils.getExtentLowerLeftX(extent)) / 2;
        const y =
            OLUtils.getExtentLowerLeftY(extent) +
            (OLUtils.getExtentUpperRightY(extent) - OLUtils.getExtentLowerLeftY(extent)) / 2;
        return [x, y];
    }

    public static generateCrossPatternBackground(backgroundColor: string, crossColor: string): CanvasPattern {
        // Create a pattern
        const patternCanvas = document.createElement('canvas');
        const patternContext = patternCanvas.getContext('2d');

        // Give the pattern a width and height of 50
        patternCanvas.width = 50;
        patternCanvas.height = 50;

        // Give the pattern a background (fill) and cross (stroke) color
        patternContext.fillStyle = backgroundColor;
        patternContext.strokeStyle = crossColor;
        patternContext.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

        //draw a cross
        patternContext.beginPath();
        patternContext.moveTo(0, 0);
        patternContext.lineTo(20, 20);
        patternContext.moveTo(20, 0);
        patternContext.lineTo(0, 20);
        patternContext.stroke();

        // Create canvas and fill it with the pattern
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        return ctx.createPattern(patternCanvas, 'repeat');
    }

    public static generateDotPatternBackground(
        backgroundColor: string,
        dotColor: string,
        dotRadius: number,
    ): CanvasPattern {
        // Create a pattern
        const patternCanvas = document.createElement('canvas');
        const patternContext = patternCanvas.getContext('2d');

        // Give the pattern a width and height of 50
        patternCanvas.width = dotRadius * 4;
        patternCanvas.height = dotRadius * 4;

        // Give the pattern a background (fill) and cross (stroke) color
        patternContext.fillStyle = backgroundColor;
        // patternContext.strokeStyle = dotColor;
        patternContext.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

        //draw a circle
        patternContext.beginPath();
        patternContext.arc(dotRadius, dotRadius, dotRadius, 0, 2 * Math.PI);
        patternContext.fillStyle = dotColor;
        patternContext.fill();
        patternContext.strokeStyle = dotColor;
        patternContext.stroke();

        // Create canvas and fill it with the pattern
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        return ctx.createPattern(patternCanvas, 'repeat');
    }
}
